import { useTranslation } from "react-i18next";
import cn from "classnames";
import { OnboardingTooltip } from "@/shared/ui";
import { getAccessRoleIconConfig } from "@/features/hr/employees/consts/accessRoleIcons";
import type { Locale } from "@/shared/utils";

const SPECIAL_ROLES = ["hr", "lawyer", "accountant", "pm"] as const;

const ZEBRA_CHIP_FILL =
  "[tr:nth-child(odd)_&]:bg-grey-50 dark:[tr:nth-child(odd)_&]:bg-grey-900 [tr:nth-child(odd):hover_&]:bg-white dark:[tr:nth-child(odd):hover_&]:bg-grey-950 [tr:nth-child(even)_&]:bg-white dark:[tr:nth-child(even)_&]:bg-grey-950";

interface Props {
  roles: string[];
  locale: Locale;
  forceBaseFill?: boolean;
  tooltipAlign?: "left" | "center" | "right";
}

export default function EmployeeAccessRoleIcons({ roles, locale, forceBaseFill = false, tooltipAlign = "left" }: Props) {
  const { t } = useTranslation("EmployeesPage");

  const specialRoles = roles.filter(
    (role) => role !== "employee" && SPECIAL_ROLES.includes(role as (typeof SPECIAL_ROLES)[number]),
  );

  if (specialRoles.length === 0) {
    return <span className="content-base-low">—</span>;
  }

  const tooltipContent = (
    <div className="flex flex-col gap-2 min-w-36">
      <p className="text-label-xs content-on-background-neutral">{t("detailModal.roles")}</p>
      {specialRoles.map(function renderTooltipItem(role) {
        const config = getAccessRoleIconConfig(role);
        const IconComponent = config?.Icon;
        const roleKey = `roleAssignment.roles.${role}.${locale}`;
        return (
          <div key={role} className="flex items-center gap-2">
            {IconComponent ? (
              <span className="shrink-0 content-on-background-neutral">
                <IconComponent size={16} color="currentColor" />
              </span>
            ) : (
              <div className="w-4 h-4 shrink-0" />
            )}
            <span className="text-body-regular-xs content-on-background-neutral">{t(roleKey)}</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <OnboardingTooltip content={tooltipContent} align={tooltipAlign}>
      <div className="flex items-center justify-start gap-2">
        {specialRoles.map(function renderRoleIcon(role) {
          const config = getAccessRoleIconConfig(role);
          if (!config) return null;
          const IconComponent = config.Icon;
          return (
            <div
              key={role}
              className={cn(
                "flex justify-center items-center w-8 h-8 radius-xs",
                forceBaseFill ? "surface-base-fill" : ZEBRA_CHIP_FILL,
              )}>
              <span className="content-action-neutral">
                <IconComponent size={16} color="currentColor" />
              </span>
            </div>
          );
        })}
      </div>
    </OnboardingTooltip>
  );
}
